// @givmo/app-charity — the memberships read's NAVIGATION trigger (CPA-WEB46).
//
// `<ActiveOrgProvider>` mounts once, at the pathless authenticated layout, and every console page
// renders under that one mount. So React Query's `refetchOnMount` — the thing that normally makes a
// short `staleTime` mean "moving around re-asks" — never fires again after the first paint: there is
// never a second observer to mount. Moving between `/donations`, `/org` and `/claims` re-asked nothing,
// which is half of why an approved claim stayed invisible until a hard reload.
//
// This leaf supplies the trigger the mount graph does not: on every in-console navigation it asks the
// memberships query to refetch IF IT IS STALE. `MEMBERSHIPS_STALE_TIME` is therefore the rate limit —
// a session clicking through five pages in as many seconds issues ONE request, not five.
//
// ⚠ IT IS NOT A POLLING LOOP, and must never become one (Jonathan's explicit ruling, 2026-09-07). Every
// refetch here is driven by an act of the person using the console; nothing fires on a timer. The stale
// window only decides whether their act is answered from cache.
//
// ⚠ IT LIVES OUTSIDE THE CONTEXT ON PURPOSE. `active-org-context.tsx` is deliberately router-free —
// dozens of feature tests render `<ActiveOrgProvider>` with no router around it, and the many pages that
// mock `@tanstack/react-router` mock only the hooks they use. Reading the router here keeps that
// property, and keeps the trigger visible in the route tree where the navigation actually happens.

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { charityAdminListMyMembershipsOptions } from '@givmo/api-client/query';

/** Refetch the memberships read on in-console navigation, when it has gone stale. Renders nothing. */
export function MembershipsFreshness() {
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // The first pathname is the mount, and this leaf does not re-ask on it.
  //
  // ⚠ ON A COLD MOUNT THAT GUARD CHANGES NOTHING, and the earlier claim that it saved a duplicate
  // request was wrong. Effects run child-before-parent, and React Query starts a query's initial fetch
  // when the OBSERVER SUBSCRIBES — inside `<ActiveOrgProvider>`'s own effect, which runs AFTER this
  // one. So at this point the query exists with no data and no fetch in flight, and a refetch here
  // would simply issue the request the observer was about to issue and the observer would join it: ONE
  // request either way (measured — removing the guard leaves the count at one). The guard earns its
  // place only where the observer is ALREADY subscribed when this leaf mounts, which is what its test
  // pins, and which is what would start double-asking if this leaf ever moved above the provider or the
  // provider gained a second observer.
  const seen = useRef<string | null>(null);

  useEffect(() => {
    if (seen.current === null) {
      seen.current = pathname;
      return;
    }
    if (seen.current === pathname) return;
    seen.current = pathname;
    void queryClient.refetchQueries({
      queryKey: charityAdminListMyMembershipsOptions().queryKey,
      // `stale: true` is the whole rate limit; `type: 'active'` keeps a torn-down observer's cache entry
      // from being refetched behind a console nobody is looking at.
      type: 'active',
      stale: true,
    });
  }, [pathname, queryClient]);

  return null;
}
